import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MdOutlineCampaign, MdOutlineWeb, MdOutlinePhoneIphone, MdOutlinePalette } from "react-icons/md";
import { getDocOnce } from '../utils/firestoreOnce';

const ICONS = [MdOutlineCampaign, MdOutlineWeb, MdOutlinePhoneIphone, MdOutlinePalette];
const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f97316', '#ec4899', '#14b8a6'];

const DEFAULT_SERVICES = [
  {
    titleAr: 'الدعاية والإعلان والتسويق الإلكتروني',
    titleEn: 'Advertising & Digital Marketing',
    shortDescAr: 'نساعدك على الوصول لعملائك وزيادة انتشار علامتك التجارية.',
    shortDescEn: 'We help you reach your customers and increase your brand awareness.',
  },
  {
    titleAr: 'تصميم وبرمجة المواقع الإلكترونية',
    titleEn: 'Website Design & Development',
    shortDescAr: 'تصميم مواقع احترافية متجاوبة مع جميع الأجهزة.',
    shortDescEn: 'Designing professional responsive websites for all devices.',
  },
  {
    titleAr: 'تطوير تطبيقات الهاتف المحمول',
    titleEn: 'Mobile App Development',
    shortDescAr: 'تطبيقات iOS و Android بجودة عالية وأداء مميز.',
    shortDescEn: 'High quality and performant iOS and Android apps.',
  },
  {
    titleAr: 'تصميم واجهات وتجربة المستخدم',
    titleEn: 'UI/UX Design',
    shortDescAr: 'تصميم واجهات عصرية وتجربة مستخدم سلسة ومميزة.',
    shortDescEn: 'Modern UI design and seamless user experience.',
  },
];

const ServicesCards = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language === 'ar' ? 'ar' : 'en';
  const isArabic = currentLanguage === 'ar';

  const [services, setServices] = useState(DEFAULT_SERVICES);

  useEffect(() => {
    let cancelled = false;
    getDocOnce('servicesPage/content')
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists() && snap.data().services?.length > 0) {
          setServices(snap.data().services);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="py-20 relative overflow-hidden z-10"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4">
        
        {/* Header */}
        <div className="text-center mb-16 relative z-10">
          <div className="inline-flex flex-col items-center justify-center relative">
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-wide mb-4">
              {isArabic ? 'خدمـاتنا' : 'Our Services'}
            </h2>
            <div className="flex items-center gap-2">
              <span className="w-6 md:w-8 h-0.5 bg-[#FAA617] rounded-full"></span>
              <span className="w-2 h-2 bg-[#0D6EFD] rounded-full shadow-[0_0_8px_#0D6EFD]"></span>
              <span className="w-6 md:w-8 h-0.5 bg-[#FAA617] rounded-full"></span>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.filter(s => s.isActive !== false).slice(0, 4).map((service, index) => {
            const Icon = ICONS[index % ICONS.length];
            const color = COLORS[index % COLORS.length];

            return (
              <div
                key={service.id || index}
                className="bg-[#0B1120] border border-white/5 rounded-2xl p-8 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_10px_40px_rgba(0,0,0,0.6)] group h-full"
              >
                {/* Neon Icon */}
                <div 
                  className="w-20 h-20 rounded-full border-2 flex items-center justify-center mb-8 transition-all duration-500 group-hover:scale-110"
                  style={{
                    borderColor: color,
                    color: color,
                    boxShadow: `0 0 15px ${color}40, inset 0 0 15px ${color}40`
                  }}
                >
                  <Icon className="text-4xl" style={{ filter: `drop-shadow(0 0 8px ${color})` }} />
                </div>

                {/* Title */}
                <h3 className="text-white font-bold text-lg md:text-xl mb-4 leading-snug">
                  {isArabic ? service.titleAr : service.titleEn}
                </h3>
                
                {/* Description */}
                <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-8 flex-grow">
                  {isArabic ? service.shortDescAr : service.shortDescEn}
                </p>

                
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ServicesCards;